import { Spinner } from '@change/ds-core-ui';

import { getFacebookLocale } from '@change-corgi/config/facebook';
import { stripHtmlTags } from '@change-corgi/core/html';
import { useKnownAvailableParams } from '@change-corgi/core-react/router';

import { withPrefetchedData } from 'src/shared/prefetch';

import type {
	DecisionMakerResponsePageData,
	DecisionMakerResponsePagePrefetchedDataProps,
} from 'src/app/pages/decisionMakerResponse/details/pageContext';
import {
	DecisionMakerResponsePageContextProvider,
	prefetchPageContext,
} from 'src/app/pages/decisionMakerResponse/details/pageContext';
import { addSchemeToUrl } from 'src/app/shared/utils/assets';
import { isLoaded } from 'src/app/shared/utils/async';
import { stripEmojis, truncate } from 'src/app/shared/utils/string';

import { Layout } from '../layout';

import { DecisionMakerResponseErrorHandler } from './ErrorHandler';

const DEFAULT_IMAGE = '/images/default-petition.gif';

type PhotoInfo = { url: string; width: number; height: number };

export const DecisionMakerResponse = withPrefetchedData(
	function DecisionMakerResponse({ prefetchedData }: DecisionMakerResponsePagePrefetchedDataProps) {
		const { petitionSlugOrId, responseId } = useKnownAvailableParams<{
			petitionSlugOrId: string;
			responseId: string;
		}>();

		return (
			<DecisionMakerResponsePageContextProvider
				petitionSlugOrId={petitionSlugOrId}
				responseId={responseId}
				prefetchedData={prefetchedData}
				errorHandler={DecisionMakerResponseErrorHandler}
				loadingFallback={<Spinner className="mx-auto my-64" />}
			>
				<Layout />
			</DecisionMakerResponsePageContextProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [Layout],
		prefetchData: async (context) =>
			// oxlint-disable-next-line typescript-eslint/no-non-null-assertion
			prefetchPageContext(context.params.petitionSlugOrId!, context.params.responseId!, context),
		pageTitle: ({ prefetchedData, i18n, fromTemplate }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			return fromTemplate(i18n.translate('fe.pages.decision_maker_response.title'));
		},
		pageMetadata: ({ prefetchedData, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return {};
			}
			const { decisionMakerResponse } = prefetchedData.pageData.pageData;
			const { message, decisionMaker, petition } = decisionMakerResponse;
			const title = decisionMaker.displayName;
			const description = truncate(stripEmojis(stripHtmlTags(message)), 400);
			const fbLocale = getFacebookLocale(petition.originalLocale.localeCode) || getFacebookLocale('en-US');
			const photo = getPhoto(decisionMakerResponse);
			return {
				ogLocale: fbLocale,
				ogTitle: title,
				ogDescription: description,
				...(photo
					? {
							ogImage: photo.url,
							ogImageSecureUrl: photo.url,
							ogImageWidth: photo.width,
							ogImageHeight: photo.height,
							twitterImage: photo.url,
						}
					: {
							ogImage: getStaticAssetUrl(DEFAULT_IMAGE),
							ogImageSecureUrl: getStaticAssetUrl(DEFAULT_IMAGE),
							ogImageWidth: 1120,
							ogImageHeight: 630,
							twitterImage: getStaticAssetUrl(DEFAULT_IMAGE),
						}),
				twitterCard: 'summary_large_image',
				twitterImageAlt: title,
			};
		},
	},
);

function getPhoto(response: DecisionMakerResponsePageData['decisionMakerResponse']): PhotoInfo | undefined {
	const { photo, embeddedMedia, petition } = response;
	if (photo?.petitionLarge) {
		return { ...photo.petitionLarge, url: addSchemeToUrl(photo.petitionLarge.url) };
	}
	if (embeddedMedia?.thumbnailUrl && embeddedMedia.thumbnailWidth && embeddedMedia.thumbnailHeight) {
		return {
			url: addSchemeToUrl(embeddedMedia.thumbnailUrl),
			width: embeddedMedia.thumbnailWidth,
			height: embeddedMedia.thumbnailHeight,
		};
	}
	if (petition.photo?.petitionLarge) {
		return { ...petition.photo.petitionLarge, url: addSchemeToUrl(petition.photo.petitionLarge.url) };
	}
	return undefined;
}
